import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge, Select, Textarea } from '@components';
import { CreateAgentData, KnowledgeBaseOption, CallRouteOption } from '../../types';
import { calendarIntegrationService } from '../../../Calendars/services/calendarIntegration';
import { Calendar } from '../../../Calendars/types';
import { useSchedulingAccess, useSchedulingProfiles } from '../../../Calendars/scheduling/hooks';
import { isSchedulingSelection, schedulingSelection } from '../../calendarSelection';
import { ApiClient } from '@utils/apiClient';
import { handleApiResult, handleApiCatch } from '@utils/apiErrorHandler';
import { SMSTemplate } from '../../../Communications/types';
import { PostalCodePicker } from '../PostalCodePicker';

interface CallSettingsTabProps {
  formData: CreateAgentData;
  onInputChange: (
    field: keyof CreateAgentData,
    value: string | number | boolean | string[] | unknown[]
  ) => void;
  onBatchInputChange?: (updates: Partial<CreateAgentData>) => void;
  errors: Record<string, string>;
  knowledgeBases: KnowledgeBaseOption[];
  callRoutes: CallRouteOption[];
  loading: boolean;
  scenarioUsesAddressValidation?: boolean;
}

const CallSettingsTab: React.FC<CallSettingsTabProps> = ({
  formData,
  onInputChange,
  onBatchInputChange,
  errors,
  knowledgeBases,
  callRoutes,
  loading,
  scenarioUsesAddressValidation = false,
}) => {
  const { t } = useTranslation();
  const [calendars, setCalendars] = useState<Calendar[]>([]);
  const [calendarsLoading, setCalendarsLoading] = useState(true);
  const [availableSmsTemplates, setAvailableSmsTemplates] = useState<SMSTemplate[]>([]);
  const [smsTemplatesLoading, setSmsTemplatesLoading] = useState(true);
  const [smsAdvancedOpen, setSmsAdvancedOpen] = useState(false);
  const postalCodes = formData.service_area_postal_codes || [];

  const handlePostalCodesChange = (codes: string[], labels: Record<string, string>) => {
    if (onBatchInputChange) {
      onBatchInputChange({ service_area_postal_codes: codes, service_area_places: labels });
    } else {
      onInputChange('service_area_postal_codes', codes);
    }
  };

  const { data: schedulingAccess } = useSchedulingAccess();
  const schedulingEnabled = schedulingAccess?.enabled ?? false;
  const { data: schedulingProfiles = [], isLoading: profilesLoading } =
    useSchedulingProfiles(schedulingEnabled);

  // The Select has no groups, so disabled headings stand in; scheduling calendars come first (recommended).
  const calendarOptions = [
    { value: '', label: t('agents.noCalendar') || 'Kein Kalender' },
    ...(schedulingEnabled
      ? [
          {
            value: '__group_scheduling',
            label: t('agents.calendarGroupScheduling'),
            disabled: true,
          },
          // Inactive calendars take no bookings; only a still assigned one stays visible.
          ...schedulingProfiles
            .filter(
              profile =>
                profile.is_active ||
                schedulingSelection(profile.id) === formData.calendar_integration
            )
            .map(profile => ({
              value: schedulingSelection(profile.id),
              label: profile.is_active ? profile.name : `${profile.name} (${t('common.inactive')})`,
            })),
          {
            value: '__group_external',
            label: t('agents.calendarGroupExternal'),
            disabled: true,
          },
        ]
      : []),
    ...calendars.map(calendar => ({
      value: calendar.id,
      label: `${calendar.name || calendar.provider_display} (${calendar.email})`,
    })),
  ];
  const selectedSchedulingProfile = isSchedulingSelection(formData.calendar_integration);
  const calendarError =
    errors.calendar_integration || errors.scheduling_profiles || errors.calendars;

  useEffect(() => {
    const fetchCalendars = async () => {
      try {
        setCalendarsLoading(true);
        const calendarsList = await calendarIntegrationService.list();
        setCalendars(calendarsList);
      } catch (error) {
        setCalendars([]);
      } finally {
        setCalendarsLoading(false);
      }
    };
    fetchCalendars();
  }, []);

  useEffect(() => {
    const fetchSmsTemplates = async () => {
      try {
        setSmsTemplatesLoading(true);
        const response = await ApiClient.get('/communications/sms-templates/');
        const result = await handleApiResult<SMSTemplate[]>(
          response,
          'Failed to fetch SMS templates'
        );
        if (result.success && result.data) {
          setAvailableSmsTemplates(result.data.filter(tpl => tpl.status === 'active'));
        }
      } catch (err) {
        handleApiCatch(err, 'Failed to fetch SMS templates');
      } finally {
        setSmsTemplatesLoading(false);
      }
    };
    fetchSmsTemplates();
  }, []);

  const selectedTemplateIds = formData.in_call_sms_templates || [];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('agents.duringTheCall')}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          {t('agents.duringTheCallDescription')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Knowledge & Documents Section */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <span className="material-icons text-primary-500 dark:text-primary-300 mr-2">
              library_books
            </span>
            <h4 className="text-md font-semibold text-gray-900 dark:text-white">
              {t('agents.knowledgeAndDocuments')}
            </h4>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {t('agents.knowledgeAndDocumentsDescription')}
          </p>

          <div>
            <label
              htmlFor="knowledge-base"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {t('agents.knowledgeBase')}
            </label>
            <Select
              id="knowledge-base"
              value={formData.knowledge_base || ''}
              onChange={value => {
                const kbId = Array.isArray(value) ? value[0] : value;
                onInputChange('knowledge_base', kbId || '');
              }}
              placeholder={t('agents.selectKnowledgeBase')}
              options={[
                { value: '', label: t('agents.selectKnowledgeBase') },
                ...knowledgeBases.map(kb => ({
                  value: kb.id,
                  label: kb.name,
                })),
              ]}
              className="w-full"
              disabled={loading}
              error={errors.knowledge_base ? true : undefined}
              errorText={errors.knowledge_base}
            />
          </div>
        </div>

        {/* Communication Section */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <span className="material-icons text-primary-500 dark:text-primary-300 mr-2">
              phone
            </span>
            <h4 className="text-md font-semibold text-gray-900 dark:text-white">
              {t('agents.communication')}
            </h4>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {t('agents.communicationDescription')}
          </p>

          <div className="space-y-6">
            {/* Call Routes */}
            <div>
              <label
                htmlFor="call-routes"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                {t('agents.callRoutes')}
              </label>
              <Select
                id="call-routes"
                value={formData.call_routes}
                onChange={value =>
                  onInputChange('call_routes', Array.isArray(value) ? value : [value])
                }
                placeholder={t('agents.selectOption')}
                options={[
                  ...callRoutes.map(route => ({
                    value: route.id,
                    label: `${route.name} (${route.phone_number})`,
                  })),
                ]}
                className="w-full"
                disabled={loading}
                multiple
                error={errors.call_routes ? true : undefined}
                errorText={errors.call_routes}
              />
            </div>
          </div>
        </div>

        {/* Calendar Integration Section */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <span className="material-icons text-primary-500 dark:text-primary-300 mr-2">
              calendar_today
            </span>
            <h4 className="text-md font-semibold text-gray-900 dark:text-white">
              {t('agents.calendar')}
            </h4>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {t('agents.calendarDescription')}
          </p>

          <div className="space-y-4">
            <div>
              <label
                htmlFor="calendar"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                {t('agents.calendarIntegration')}
              </label>
              <Select
                id="calendar"
                value={formData.calendar_integration || ''}
                onChange={value => {
                  const selectedValue = Array.isArray(value) ? value[0] : value;
                  onInputChange('calendar_integration', selectedValue || '');
                }}
                placeholder={t('agents.selectOption')}
                options={calendarOptions}
                className="w-full"
                disabled={calendarsLoading || profilesLoading}
                error={calendarError ? true : undefined}
                errorText={calendarError}
              />
              {schedulingEnabled && !profilesLoading && schedulingProfiles.length === 0 && (
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  {t('agents.noSchedulingCalendars')}{' '}
                  <Link to="/calendars" className="text-primary-600 hover:underline">
                    {t('agents.createSchedulingCalendar')}
                  </Link>
                </p>
              )}
              {schedulingEnabled && formData.calendar_integration && !selectedSchedulingProfile && (
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  {t('agents.externalCalendarHint')}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Web Search Section */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <span className="material-icons text-primary-500 dark:text-primary-300 mr-2">
              search
            </span>
            <h4 className="text-md font-semibold text-gray-900 dark:text-white">
              {t('agents.webSearch')}
            </h4>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {t('agents.webSearchDescription')}
          </p>

          <div className="space-y-4">
            <div>
              <label
                htmlFor="web-search"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                {t('agents.webSearch')}
              </label>
              <Select
                id="web-search"
                value={formData.web_search_enabled ? 'enabled' : 'disabled'}
                onChange={value => {
                  const webSearchValue = Array.isArray(value) ? value[0] : value;
                  onInputChange('web_search_enabled', webSearchValue === 'enabled');
                }}
                options={[
                  { value: 'disabled', label: t('agents.disabled') },
                  { value: 'enabled', label: t('agents.enabled') },
                ]}
                className="w-full"
              />
            </div>

            {formData.web_search_enabled && (
              <>
                <div>
                  <label
                    htmlFor="search-scope"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {t('agents.searchScope')}
                  </label>
                  <div className="space-y-2">
                    <label htmlFor="search-scope-whitelist" className="flex items-center">
                      <input
                        id="search-scope-whitelist"
                        type="radio"
                        name="webSearchScope"
                        value="whitelist"
                        checked={!formData.all_internet}
                        onChange={() => onInputChange('all_internet', false)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {t('agents.whitelistWebsites')}
                      </span>
                    </label>
                    <label htmlFor="search-scope-all" className="flex items-center">
                      <input
                        id="search-scope-all"
                        type="radio"
                        name="webSearchScope"
                        value="all"
                        checked={formData.all_internet}
                        onChange={() => onInputChange('all_internet', true)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {t('agents.allInternet')}
                      </span>
                    </label>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="whitelisted-websites"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {t('agents.whitelistedWebsites')}
                  </label>
                  <Textarea
                    id="whitelisted-websites"
                    className="w-full"
                    rows={3}
                    value={formData.whitelist_websites || ''}
                    onChange={e => onInputChange('whitelist_websites', e.target.value)}
                    placeholder="Enter website URLs (one per line):&#10;https://example.com&#10;https://docs.example.com"
                    state={errors.whitelist_websites ? 'error' : 'default'}
                    errorText={errors.whitelist_websites}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* AI Rejection Section */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <span className="material-icons text-primary-500 dark:text-primary-300 mr-2">
              support_agent
            </span>
            <h4 className="text-md font-semibold text-gray-900 dark:text-white">
              {t('agents.aiRejection.title')}
            </h4>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {t('agents.aiRejection.description')}
          </p>

          <div className="space-y-4">
            <div>
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('agents.aiRejection.actionLabel')}
              </span>
              <div className="space-y-2">
                <label htmlFor="ai-rejection-hangup" className="flex items-center">
                  <input
                    id="ai-rejection-hangup"
                    type="radio"
                    name="aiRejectionAction"
                    value="hangup"
                    checked={(formData.ai_rejection_action || 'hangup') === 'hangup'}
                    onChange={() => onInputChange('ai_rejection_action', 'hangup')}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {t('agents.aiRejection.actionHangup')}
                  </span>
                </label>
                <label htmlFor="ai-rejection-transfer" className="flex items-center">
                  <input
                    id="ai-rejection-transfer"
                    type="radio"
                    name="aiRejectionAction"
                    value="transfer"
                    checked={formData.ai_rejection_action === 'transfer'}
                    onChange={() => onInputChange('ai_rejection_action', 'transfer')}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {t('agents.aiRejection.actionTransfer')}
                  </span>
                </label>
                <label htmlFor="ai-rejection-disabled" className="flex items-center">
                  <input
                    id="ai-rejection-disabled"
                    type="radio"
                    name="aiRejectionAction"
                    value="disabled"
                    checked={formData.ai_rejection_action === 'disabled'}
                    onChange={() => onInputChange('ai_rejection_action', 'disabled')}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {t('agents.aiRejection.actionDisabled')}
                  </span>
                </label>
              </div>
            </div>

            {formData.ai_rejection_action === 'disabled' && (
              <div className="flex items-start rounded-md border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/30 p-3">
                <span className="material-icons text-amber-600 dark:text-amber-400 mr-2 text-base">
                  warning
                </span>
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  {t('agents.aiRejection.disabledWarning')}
                </p>
              </div>
            )}

            {formData.ai_rejection_action === 'transfer' && (
              <>
                <div>
                  <label
                    htmlFor="ai-rejection-transfer-route"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {t('agents.aiRejection.transferRoute')}
                  </label>
                  <Select
                    id="ai-rejection-transfer-route"
                    value={formData.ai_rejection_transfer_route || ''}
                    onChange={value => {
                      const routeId = Array.isArray(value) ? value[0] : value;
                      onInputChange('ai_rejection_transfer_route', routeId || '');
                    }}
                    placeholder={t('agents.selectOption')}
                    options={[
                      ...callRoutes.map(route => ({
                        value: route.id,
                        label: `${route.name} (${route.phone_number})`,
                      })),
                    ]}
                    className="w-full"
                    disabled={loading}
                    error={errors.ai_rejection_transfer_route ? true : undefined}
                    errorText={errors.ai_rejection_transfer_route}
                  />
                </div>

                <div>
                  <label
                    htmlFor="ai-rejection-transfer-message"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {t('agents.aiRejection.transferMessage')}
                  </label>
                  <Textarea
                    id="ai-rejection-transfer-message"
                    className="w-full"
                    rows={3}
                    maxLength={500}
                    value={formData.ai_rejection_transfer_message || ''}
                    onChange={e => onInputChange('ai_rejection_transfer_message', e.target.value)}
                    placeholder={t('agents.aiRejection.transferMessagePlaceholder')}
                    state={errors.ai_rejection_transfer_message ? 'error' : 'default'}
                    errorText={errors.ai_rejection_transfer_message}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('agents.aiRejection.transferMessageHelp')}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* In-Call SMS Section */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center">
              <span className="material-icons text-primary-500 dark:text-primary-300 mr-2">
                sms
              </span>
              <h4 className="text-md font-semibold text-gray-900 dark:text-white">
                {t('agents.inCallSms.title')}
              </h4>
            </div>
            <label
              htmlFor="in-call-sms"
              className="relative inline-flex items-center cursor-pointer ml-3 shrink-0"
            >
              <span className="sr-only">{t('agents.inCallSms.title')}</span>
              <input
                id="in-call-sms"
                type="checkbox"
                className="sr-only peer"
                checked={formData.in_call_sms_enabled ?? false}
                onChange={e => onInputChange('in_call_sms_enabled', e.target.checked)}
              />
              <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-checked:bg-primary-600 rounded-full peer transition-colors peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600"></div>
            </label>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {t('agents.inCallSms.description')}
          </p>

          <div className="space-y-4">
            {formData.in_call_sms_enabled && (
              <>
                {/* Block ①: Inhalte */}
                <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                  {t('agents.inCallSms.contentSection')}
                </h5>

                <div>
                  <label
                    htmlFor="in-call-sms-templates"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {t('agents.inCallSms.templates')}
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    {t('agents.inCallSms.templatesHelp')}
                  </p>
                  {availableSmsTemplates.length === 0 ? (
                    <p className="text-sm text-gray-400 dark:text-gray-500 italic">
                      {smsTemplatesLoading
                        ? t('common.loading')
                        : t('agents.inCallSms.noAvailableTemplates')}
                    </p>
                  ) : (
                    <Select
                      id="in-call-sms-templates"
                      multiple
                      value={selectedTemplateIds}
                      onChange={value =>
                        onInputChange(
                          'in_call_sms_templates',
                          Array.isArray(value) ? value : [value]
                        )
                      }
                      options={availableSmsTemplates.map(tpl => ({
                        value: tpl.id,
                        label: tpl.title,
                      }))}
                      placeholder={t('agents.inCallSms.selectTemplates')}
                      className="w-full"
                    />
                  )}
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    {t('agents.inCallSms.manageHint')}
                  </p>
                </div>

                <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-700/50 rounded-lg">
                  <div className="flex-1">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('agents.inCallSms.freetextLabel')}
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {t('agents.inCallSms.freetextHelp')}
                    </p>
                  </div>
                  <label
                    htmlFor="in-call-sms-freetext"
                    className="relative inline-flex items-center cursor-pointer ml-3"
                  >
                    <span className="sr-only">{t('agents.inCallSms.freetextLabel')}</span>
                    <input
                      id="in-call-sms-freetext"
                      type="checkbox"
                      className="sr-only peer"
                      checked={formData.in_call_sms_freetext_enabled ?? false}
                      onChange={e =>
                        onInputChange('in_call_sms_freetext_enabled', e.target.checked)
                      }
                    />
                    <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-checked:bg-primary-600 rounded-full peer transition-colors peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600"></div>
                  </label>
                </div>

                {formData.in_call_sms_freetext_enabled && (
                  <div className="pl-4 border-l-2 border-gray-200 dark:border-gray-600">
                    <label
                      htmlFor="in-call-sms-freetext-prompt"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      {t('agents.inCallSms.freetextPromptLabel')}
                    </label>
                    <Textarea
                      id="in-call-sms-freetext-prompt"
                      className="w-full"
                      rows={3}
                      value={formData.in_call_sms_freetext_prompt || ''}
                      onChange={e => onInputChange('in_call_sms_freetext_prompt', e.target.value)}
                      placeholder={t('agents.inCallSms.freetextPromptPlaceholder')}
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {t('agents.inCallSms.freetextPromptHelp')}
                    </p>
                  </div>
                )}

                {/* Block ②: Erweitert (einklappbar) */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <button
                    type="button"
                    onClick={() => setSmsAdvancedOpen(open => !open)}
                    className="flex items-center w-full text-left text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  >
                    <span className="material-icons text-base mr-1">
                      {smsAdvancedOpen ? 'expand_more' : 'chevron_right'}
                    </span>
                    {t('agents.inCallSms.advancedSection')}
                  </button>

                  {smsAdvancedOpen && (
                    <div className="space-y-4 mt-4">
                      <div>
                        <label
                          htmlFor="in-call-sms-policy-prompt"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                          {t('agents.inCallSms.policyPromptLabel')}
                        </label>
                        <Textarea
                          id="in-call-sms-policy-prompt"
                          className="w-full"
                          rows={3}
                          value={formData.in_call_sms_policy_prompt || ''}
                          onChange={e => onInputChange('in_call_sms_policy_prompt', e.target.value)}
                          placeholder={t('agents.inCallSms.policyPromptPlaceholder')}
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {t('agents.inCallSms.policyPromptHelp')}
                        </p>
                      </div>

                      <div>
                        <label
                          htmlFor="in-call-sms-max"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                          {t('agents.inCallSms.maxPerCall')}
                        </label>
                        <input
                          id="in-call-sms-max"
                          type="number"
                          min={1}
                          max={10}
                          value={formData.in_call_sms_max_per_call ?? 3}
                          onChange={e =>
                            onInputChange(
                              'in_call_sms_max_per_call',
                              Math.max(1, Math.min(10, Number(e.target.value) || 1))
                            )
                          }
                          className="w-24 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {t('agents.inCallSms.maxPerCallHelp')}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Service Area & Address Check Section */}
        <div
          className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6"
          data-testid="service-area-section"
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center">
              <span className="material-icons text-primary-500 dark:text-primary-300 mr-2">
                pin_drop
              </span>
              <h4 className="text-md font-semibold text-gray-900 dark:text-white">
                {t('agents.serviceArea.title')}
              </h4>
            </div>
            <label
              htmlFor="address-validation"
              className="relative inline-flex items-center cursor-pointer ml-3 shrink-0"
            >
              <span className="sr-only">{t('agents.serviceArea.title')}</span>
              <input
                id="address-validation"
                type="checkbox"
                className="sr-only peer"
                checked={formData.address_validation_enabled ?? false}
                onChange={e => onInputChange('address_validation_enabled', e.target.checked)}
              />
              <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-checked:bg-primary-600 rounded-full peer transition-colors peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600"></div>
            </label>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {t('agents.serviceArea.description')}
          </p>

          {formData.address_validation_enabled && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label
                  htmlFor="service-area-postal-codes"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  {t('agents.serviceArea.postalCodes')}
                </label>
                <Badge variant="outline" size="sm">
                  {t('agents.serviceArea.postalCodesCount', { count: postalCodes.length })}
                </Badge>
              </div>
              <PostalCodePicker
                id="service-area-postal-codes"
                value={postalCodes}
                labels={formData.service_area_places || {}}
                onChange={handlePostalCodesChange}
                errorText={errors.service_area_postal_codes}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('agents.serviceArea.postalCodesHelp')}
              </p>
              {postalCodes.length === 0 && (
                <p className="text-xs text-warning-700 dark:text-warning-300">
                  {t('agents.serviceArea.emptyAreaHint')}
                </p>
              )}
              {scenarioUsesAddressValidation && (
                <div className="flex items-start gap-2 rounded-md border border-info-200 bg-info-50 p-3 text-xs text-info-700 dark:border-info-800 dark:bg-info-900/20 dark:text-info-300">
                  <span className="material-icons text-base">info</span>
                  <span>{t('agents.serviceArea.scenarioHint')}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { CallSettingsTab };
